import React, { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import './Home.scss';
import { DeviceCounts, ISidebarRouteState, StageStateBar, StageStateIcon, StateBar, StateIcon } from 'components';
import { StageState, UpdateState } from 'enums';
import { IUpdateWithFile } from 'interfaces';
import { ManufacturersService, UpdateTypesService, UsersService } from 'services';
import { formatDate, formatTime } from 'services/Utils';
import { useLuxonTimezone } from 'hooks';

interface IListStageProps {
    linkRouteState: ISidebarRouteState,
    stageIndex: number,
    update: IUpdateWithFile,
}

export function ListStage({ linkRouteState, stageIndex, update }: IListStageProps) {
    const { t } = useTranslation();
    const { hasTimezone, timezone } = useLuxonTimezone(UsersService.me.timezone);
    const stage = update.rollout.stages[stageIndex];

    let bar: ReactNode;
    let icon: ReactNode;
    if (update.state < UpdateState.Running) {
        bar = <StateBar state={update.state} />
        icon = <StateIcon state={update.state} />
    } else {
        bar = <StageStateBar state={stage.state} />
        icon = <StageStateIcon state={stage.state} />
    }

    let counts = update.counts.stages[stageIndex];
    if (!counts) {
        counts = {total: 0, totalLocations: 0, inProgress: 0, success: 0, error: 0, isValid: false};
    }

    const uf = update.updateFile;
    const subtitle = (ManufacturersService.manufacturersMap[update.manufacturerId]?.name || 'No Manufacturer')
        + ' / ' + (UpdateTypesService.updateTypesMap[uf?.updateTypeId]?.description || 'No Type')
        + ' / ' + (uf?.version || 'No Version');

    const startDateOnly = formatDate(stage.startTime, timezone);
    const startTimeOnly = formatTime(stage.startTime, timezone, hasTimezone);
    const endDateOnly = formatDate(stage.stopTime, timezone);
    const endTimeOnly = formatTime(stage.stopTime, timezone, hasTimezone);

    return (
        <Link to={{
            pathname: '/updates/' + update.id,
            state: linkRouteState,
        }} className="home_listStage">
            {bar}
            <div className="home_listCol home_listColTitles">
                <div className="home_listTitle ellipsis" title={update.name}>
                    {icon} {update.name} {stageIndex + 1}/{update.rollout.stages.length}
                </div>
                <div className="home_listSubtitle ellipsis" title={subtitle}>
                    {subtitle}
                </div>
            </div>
            <div className="home_listCol home_listColDates">
                <div className="home_listDateFrom" title={startDateOnly + ', ' + startTimeOnly}>
                    <div className="home_listDate ellipsis">
                        {startDateOnly}
                    </div>
                    <div className="ellipsis">
                        {startTimeOnly}
                    </div>
                </div>
                <FontAwesomeIcon icon="ellipsis-h" className="home_listDateSeparator" />
                <div className="home_listDateTo" title={endDateOnly + ', ' + endTimeOnly}>
                    <div className="home_listDate ellipsis">
                        {endDateOnly}
                    </div>
                    <div className="ellipsis">
                        {endTimeOnly}
                    </div>
                </div>
            </div>
            <div className="home_listCol home_listColStatus">
                <DeviceCounts
                    animateInProgress={update.state === UpdateState.Running}
                    counts={counts}
                    hasStarted={stage.state >= StageState.Running}
                />
            </div>
        </Link>
    );
}